// Vocabulary data loaded from chapter files
let vocabularyList = [];
let isLoaded = false;
let loadPromise = null;

const CHAPTERS = [1, 2, 3, 4, 5, 6, 7, 8];

/**
 * Load settings from localStorage
 * @returns {Object} Settings with selectedChapters and selectedPages
 */
function loadSettings() {
  const saved = localStorage.getItem('vocabSettings');
  if (saved) {
    return JSON.parse(saved);
  }
  // Default: chapter 1 selected
  return {
    selectedChapters: [1],
    selectedPages: {},
    vetOnly: false,
    verbsOnly: false
  };
}

/**
 * Check if an Italian word is a verb based on various patterns
 * @param {string} italian - The Italian word/phrase
 * @returns {boolean} True if it's a verb
 */
function isVerb(italian) {
  if (!italian) return false;
  
  const lower = italian.toLowerCase();
  
  // Check for past participle notation (pp)
  if (lower.includes(' pp ')) return true;
  
  // Check for auxiliary verb markers
  if (/\b(essere|ess|avere|avr)\b/.test(lower)) return true;
  
  // Check for reflexive endings
  if (lower.endsWith('arsi') || lower.endsWith('ersi') || lower.endsWith('irsi')) return true;
  
  // Check for reflexive pronoun indicators in parentheses
  if (/\((mi|ti|si|ci|vi)\s/.test(lower)) return true;
  
  // Check for object markers combined with verb endings
  // Verbs typically end in -are, -ere, -ire and have qn (qualcuno) or qc (qualcosa) markers
  const hasVerbEnding = /\b\w+(are|ere|ire)\b/.test(lower);
  if (hasVerbEnding) {
    // If it has object markers, it's likely a verb
    if (lower.includes(' qn') || lower.includes(' qc')) return true;
    // If it has an asterisk, it's likely an irregular verb
    if (lower.includes('*')) return true;
  }
  
  return false;
}

/**
 * Load vocabulary from the chapter JSON files based on settings
 * @returns {Promise<Array>} The loaded vocabulary list
 */
export async function loadVocabulary() {
  if (isLoaded) {
    return vocabularyList;
  }
  
  if (loadPromise) {
    return loadPromise;
  }
  
  loadPromise = _loadAllVocabulary();
  return loadPromise;
}

async function _loadAllVocabulary() {
  const settings = loadSettings();
  const selectedChapters = settings.selectedChapters || [1];
  const selectedPages = settings.selectedPages || {};
  
  console.log('Loading vocabulary for chapters:', selectedChapters);
  console.log('Current path:', window.location.pathname);
  console.log('Full URL:', window.location.href);
  
  vocabularyList = [];
  let idCounter = 1;
  
  for (const chapter of selectedChapters) {
    const url = `./resources/${chapter}.json`;
    console.log(`Fetching: ${url}`);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`Failed to load chapter ${chapter}: ${response.status} from ${url}`);
        continue;
      }
      
      const chapterData = await response.json();
      
      // Filter pages based on settings
      const pagesToInclude = selectedPages[chapter] || chapterData.map(p => p.page);
      
      for (const pageData of chapterData) {
        if (!pagesToInclude.includes(pageData.page)) {
          continue; // Skip pages not selected
        }
        
        for (const item of pageData.vocabulary) {
          // Skip non-bold words if vetOnly filter is enabled
          if (settings.vetOnly && !item.bold) {
            continue;
          }
          
          // Skip non-verbs if verbsOnly filter is enabled
          if (settings.verbsOnly && !isVerb(item.italian)) {
            continue;
          }
          
          vocabularyList.push({
            id: idCounter++,
            word: item.dutch,
            meaning: item.italian,
            example: '',
            pronunciation: '',
            partOfSpeech: '',
            difficulty: 'medium',
            chapter: chapter,
            page: pageData.page,
            isBold: item.bold || false
          });
        }
      }
    } catch (error) {
      console.error(`Error loading chapter ${chapter}:`, error);
    }
  }
  
  console.log(`Loaded ${vocabularyList.length} words from ${selectedChapters.length} chapter(s)`);
  
  isLoaded = true;
  return vocabularyList;
}

/**
 * Reload vocabulary (useful after settings change)
 */
export function reloadVocabulary() {
  isLoaded = false;
  loadPromise = null;
  vocabularyList = [];
  return loadVocabulary();
}

/**
 * Get the vocabulary list (loads if not already loaded)
 * @returns {Promise<Array>} The vocabulary list
 */
export async function getVocabularyList() {
  if (!isLoaded) {
    await loadVocabulary();
  }
  return vocabularyList;
}

/**
 * Get random words from the vocabulary
 * @param {number} count - Number of words to get
 * @returns {Promise<Array>} Random words
 */
export async function getRandomWords(count = 10) {
  const words = await getVocabularyList();
  if (words.length === 0) {
    return [];
  }
  
  // If we have fewer words than requested, fill with duplicates
  if (words.length < count) {
    const result = [];
    // Add all available words first
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    result.push(...shuffled);
    
    // Fill remaining slots with random words (allowing duplicates)
    while (result.length < count) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      // Create a copy with a unique id to avoid React/Lit key conflicts
      result.push({
        ...randomWord,
        id: `${randomWord.id}-dup-${result.length}`
      });
    }
    
    return result;
  }
  
  // If we have enough words, return random selection
  const shuffled = [...words].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Get a word by its ID
 * @param {number} id - The word ID
 * @returns {Promise<Object|null>} The word object or null
 */
export async function getWordById(id) {
  const words = await getVocabularyList();
  return words.find(word => word.id === id) || null;
}

/**
 * Get all available chapters
 * @returns {Array} Array of chapter numbers
 */
export function getChapters() {
  return CHAPTERS;
}

// Export the list (for backward compatibility, though async functions are preferred)
export { vocabularyList };
